
import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { Form } from '@/components/form/Form';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { SmartUserSelector } from '@/components/form/fields/SmartUserSelector';
import { Counter } from '@/components/form/fields/number/Counter';
import { SelectInput } from '@/components/form/fields/select/SelectInput';
import { getCategoryIcon } from '@/components/icons/CategoryIcons';
import { Picture } from '@/components/image/Image';
import { useModal } from '@/components/modal/useModal';
import InfoTooltip from '@/components/tooltip/InfoTooltip';
import UnsavedChanges from '@/components/unsavedChanges/UnsavedChanges';
import { useDirtyFormGuard } from '@/hooks/useDirtyFormGuard';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { ServiceRequestLayout } from '@/layouts/ServiceRequestLayout';
import { HaloEquipmentDto } from '@/services/api/contracts/companies';
import { useCategories, useGetRateCards } from '@/services/api/hooks/catalog';
import { useCompany, useGetCompanyHaloEquipment, useGetCompanyHaloStock, useGetCustomerBundles, useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { useProducts } from '@/services/api/hooks/product';
import { useCreateServiceRequest } from '@/services/api/hooks/serviceRequests';
import { RoleType, useUser } from '@/services/authentication/hooks/user';
import { resolveDeployOverrideRateCardId, resolveDeployProcessingRateCardId, resolveReturnOverrideRateCardId, resolveReturnProcessingRateCardId } from '@/utils/categoryOverrides';
import { formatCurrency } from '@/utils/currency';
import { parseCollectedItemString } from '@/utils/serviceRequests';
import { zodResolver } from '@hookform/resolvers/zod';
import { PackagePlus } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaHeadset } from 'react-icons/fa';
import { FiAlertTriangle, FiCheck, FiGlobe, FiSettings, FiShield } from 'react-icons/fi';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { useSandbox } from '../../contexts/SandboxContext';
import { TechRefreshModal } from './TechRefreshModal';

const HardwareCollectionSection: FC<{
  triggerTypes: string[];
  deviceTriggers: string[];
  peripheralTriggers: string[];
  handleTriggerChange: (t: string, qty: number, field: any) => void;
  isTypeSerialized: (t: string) => boolean;
  methods: any;
}> = ({ triggerTypes, deviceTriggers, peripheralTriggers, handleTriggerChange, isTypeSerialized, methods }) => {
  const renderTriggerRow = (t: string) => {
    const qty = methods.watch(t) || 0;
    const isSerialized = isTypeSerialized(t);
    const showTable = isSerialized && qty > 0;

    return (
      <div key={t} className="flex flex-col border-b last:border-b-0">
        <div className="flex flex-row items-center justify-between py-2 px-4 hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-4">
            <div className="text-2xl text-gray-500 w-8 flex justify-center">
              {getCategoryIcon(t)}
            </div>
            <span className="font-medium capitalize text-gray-700">{t}</span>
          </div>
          <Controller
            name={t}
            control={methods.control}
            render={({ field }) => (
              <Counter 
                {...field} 
                id={t}
                onChange={(val) => handleTriggerChange(t, val, field)} 
                min={0}
              />
            )}
          />
        </div>
        {showTable && (
          <div className="px-4 pb-4">
            <table className="w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="font-bold text-left px-3 py-2 text-gray-700">Make / Model <span className="text-red-500">*</span></th>
                  <th className="font-bold text-left px-3 py-2 text-gray-700">Serial Number <span className="text-red-500">*</span></th>
                  <th className="font-bold text-left px-3 py-2 text-gray-700">Asset Tag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {Array.from({ length: qty }).map((_, i) => (
                  <tr key={i}>
                    <td className="p-1 border-r border-gray-200">
                      <Controller
                        name={`assets.${t}.${i}.makeModel`}
                        control={methods.control}
                        render={({ field, fieldState }) => (
                          <div className="flex flex-col">
                            <input
                              {...field}
                              className={`w-full bg-transparent border-0 focus:ring-0 px-2 py-1 ${fieldState.error ? 'text-red-900 placeholder-red-300 bg-red-50' : ''}`}
                              placeholder="Make/Model"
                            />
                          </div>
                        )}
                      />
                    </td>
                    <td className="p-1 border-r border-gray-200">
                      <Controller
                        name={`assets.${t}.${i}.serialNumber`}
                        control={methods.control}
                        render={({ field, fieldState }) => (
                          <div className="flex flex-col">
                            <input
                              {...field}
                              className={`w-full bg-transparent border-0 focus:ring-0 px-2 py-1 ${fieldState.error ? 'text-red-900 placeholder-red-300 bg-red-50' : ''}`}
                              placeholder="Serial Number"
                            />
                          </div>
                        )}
                      />
                    </td>
                    <td className="p-1">
                      <Controller
                        name={`assets.${t}.${i}.assetTag`}
                        control={methods.control}
                        render={({ field }) => (
                          <input
                            {...field}
                            className="w-full bg-transparent border-0 focus:ring-0 px-2 py-1 placeholder:italic placeholder:text-gray-400"
                            placeholder="Optional"
                          />
                        )}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="border border-gray-200 rounded bg-white overflow-hidden">
        {deviceTriggers.length > 0 && (
          <div>
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
              <h5 className="text-sm font-semibold text-gray-500 uppercase">Devices</h5>
            </div>
            <div className="divide-y divide-gray-200 border-b border-gray-200">
              {deviceTriggers.map(renderTriggerRow)}
            </div>
          </div>
        )}
        
        {peripheralTriggers.length > 0 && (
          <div>
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
              <h5 className="text-sm font-semibold text-gray-500 uppercase">Peripherals</h5>
            </div>
            <div className="divide-y divide-gray-200">
              {peripheralTriggers.map(renderTriggerRow)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const getAdminTooltipText = (companyName: string) => (
  <div className="flex flex-col gap-3 text-left text-sm w-[540px]">
    <div className="p-4 rounded-md border border-gray-200 bg-gray-50">
      <h6 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
        <FaHeadset className="w-4 h-4 text-[#41D2F1]" />
        Ticket Labour
      </h6>
      <ul className="space-y-4">
        {/* Outbound Processes */}
        <li>
          <div className="font-semibold text-gray-800 mb-2">Outbound Processes</div>
          <ul className="space-y-2 ml-2">
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <span className="text-gray-700">Create user</span>
            </li>
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <span className="text-gray-700">Assign products to user</span>
            </li>
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <div className="text-gray-700 w-full">
                <span className="block mb-2">Comms - Inform user:</span>
                <ul className="space-y-1.5 ml-1 border-l-2 border-emerald-500 pl-4">
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    Of the shipment from {companyName}
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    Products they will receive
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    Tracking details
                  </li>
                </ul>
              </div>
            </li>
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <span className="text-gray-700">Track until delivered</span>
            </li>
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <span className="text-gray-700">Confirm with user physical receipt of goods</span>
            </li>
          </ul>
        </li>

        {/* Inbound Processes */}
        <li>
          <div className="font-semibold text-gray-800 mb-2">Inbound Processes</div>
          <ul className="space-y-2 ml-2">
            <li className="flex items-start gap-2">
              <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <div className="text-gray-700 w-full">
                <span className="block mb-2">Instructions:</span>
                <ul className="space-y-1.5 ml-1 border-l-2 border-emerald-500 pl-4">
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    How to package the equipment
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                    How to book home/office collection at their convenience
                  </li>
                </ul>
              </div>
            </li>
            {[
              "Chaser 1 - Email",
              "Chaser 2 - Email",
              "Chaser 3 - Phone call",
              "Receive goods on system",
              "Check serials match",
              "Check and apply customer lifecycle policies",
              "Assign to correct processing path",
              "Unassign products from user"
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </li>
      </ul>
    </div>
  </div>
);

const logisticsTooltipText = (
  <div className="flex flex-col gap-3 text-left text-sm w-[540px]">
    <div className="p-3 rounded-md border border-gray-200 bg-gray-50 flex items-start gap-3">
      <FiGlobe className="w-5 h-5 text-[#41D2F1] mt-0.5 shrink-0" />
      <p className="text-gray-700 leading-relaxed">
        For US and EU regions, there is one average logistics cost to cover the whole region. This gives predictable costs and reduces admin.
      </p>
    </div>
    <div className="p-3 rounded-md border border-gray-200 bg-gray-50 flex items-start gap-3">
      <FiShield className="w-5 h-5 text-[#EB6626] mt-0.5 shrink-0" />
      <p className="text-gray-700 leading-relaxed">
        All goods are insured for their full amount whilst in transit and in our warehousing locations.
      </p>
    </div>
  </div>
);

const processingTooltipText = (
  <div className="flex flex-col gap-3 text-left text-sm w-[540px]">
    <div className="p-4 rounded-md border border-gray-200 bg-gray-50">
      <h6 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
        <FiSettings className="w-4 h-4 text-[#EB6626]" />
        Goods Receiving Fee
      </h6>
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
          <div className="text-gray-700 w-full">
            <span className="block mb-2">Goods receiving process:</span>
            <ul className="space-y-1.5 ml-1 border-l-2 border-emerald-500 pl-4">
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Scan goods in
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Cross check serials to match
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Allocate unique stocking ID
              </li>
              <li className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-1 h-1 rounded-full bg-emerald-500 shrink-0" />
                Shelve
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
    <div className="p-4 rounded-md border border-gray-200 bg-gray-50">
      <h6 className="font-bold text-gray-900 text-base mb-3 flex items-center gap-2">
        <PackagePlus className="w-4 h-4 text-[#41D2F1]" />
        Additional Items
      </h6>
      <ul className="space-y-2">
        <li className="flex items-start gap-2">
          <FiCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
          <span className="text-gray-700">Additional items added to consignments</span>
        </li>
      </ul>
    </div>
  </div>
);

export const TechRefreshPage: FC = () => {
  const breadcrumbs: NavPage[] = [
    { name: 'Management', to: '' },
    { name: 'Tech Refresh', to: '' }
  ];

  const { partnerId = '', companyId = '' } = useParams<{ partnerId: string; companyId?: string }>();
  const navigate = useNavigate();
  const user = useUser();
  const isAdmin = user.roles?.some((r: any) => r.type === RoleType.Admin);
  const [partner] = useMspDetailsForCustomer(partnerId, companyId);
  const createServiceRequest = useCreateServiceRequest();
  
  if (!(partnerId && companyId)) throw new Error('Missing params');
  
  const [company] = useCompany(partnerId, companyId);
  
  // -- Data Fetching --
  const [rateCards] = useGetRateCards(undefined, partnerId, companyId); // Fetch entire catalog
  const [equipmentPayload] = useGetCompanyHaloEquipment(partnerId, companyId);
  const [haloStock] = useGetCompanyHaloStock(partnerId, companyId);
  const [bundles] = useGetCustomerBundles(partnerId, companyId);
  const [catalogProducts] = useProducts();
  const [categories] = useCategories();

  // -- Rate Card Filters --
  const availableCollectionCards = useMemo(() => {
    return rateCards?.items?.filter(rc => rc.propertyBag?.applicableRequestTypes?.includes('OffboardUser')) || [];
  }, [rateCards]);

  const availableRefreshCards = useMemo(() => {
    return rateCards?.items?.filter(rc => rc.propertyBag?.applicableRequestTypes?.includes('TechRefresh')) || [];
  }, [rateCards]);

  const availableDeploymentCards = useMemo(() => {
    return rateCards?.items?.filter(rc => rc.propertyBag?.applicableRequestTypes?.includes('DeployInventory')) || [];
  }, [rateCards]);

  // -- Halo User Equipment --
  const uniqueUsers = useMemo(() => {
    if (!equipmentPayload) return [];
    const rawUsers = Array.from(new Set(equipmentPayload.map((e: HaloEquipmentDto) => e.user))).filter(Boolean) as string[];
    
    return rawUsers.map(raw => {
      const parts = raw.split('|').map(p => p.trim());
      return {
        name: parts[0] || raw,
        email: parts.length > 1 && parts[1].includes('@') ? parts[1] : null,
        rawValue: raw
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [equipmentPayload]);

  const isTypeSerialized = useCallback((t: string) => {
    return categories?.find(c => {
      let cName = c.name.toLowerCase().trim();
      if (cName.endsWith('s')) cName = cName.slice(0, -1);
      let nt = t.toLowerCase().trim();
      if (nt.endsWith('s')) nt = nt.slice(0, -1);
      return cName === nt;
    })?.isSerialized || false;
  }, [categories]);

  // -- Trigger Types for Collection (Category Engine) --
  const triggerTypes = useMemo(() => {
    return (categories || [])
      .filter(c => !c.hideFromGenerics)
      .map(c => c.name)
      .sort();
  }, [categories]);

  const { deviceTriggers, peripheralTriggers } = useMemo(() => {
    const devices = triggerTypes.filter(t => isTypeSerialized(t));
    const peripherals = triggerTypes.filter(t => !isTypeSerialized(t));
    return { deviceTriggers: devices, peripheralTriggers: peripherals };
  }, [triggerTypes, isTypeSerialized]);

  // -- Deployment State --
  const [cart, setCart] = useState<Record<string, number>>({});
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);

  const bundleOptions = useMemo(() => {
    return (bundles || []).map(b => ({ id: b.id, name: b.name }));
  }, [bundles]);

  const aggregatedHaloStock = useMemo(() => {
    if (!haloStock) return [];
    const acc: Record<string, any> = {};
    for (const item of haloStock) {
      if (!item.sku) continue;
      if (!acc[item.sku]) {
        acc[item.sku] = { ...item, stockLocations: new Set(item.stockLocation ? [item.stockLocation] : []), stockBreakdown: [{ site: item.stockLocation || item.siteID || 'Unknown', qty: item.stockQuantity }] };
      } else {
        acc[item.sku].stockQuantity += item.stockQuantity;
        if (item.stockLocation) {
          acc[item.sku].stockLocations.add(item.stockLocation);
        }
        const siteName = item.stockLocation || item.siteID || 'Unknown';
        const existingSite = acc[item.sku].stockBreakdown.find((s: any) => s.site === siteName);
        if (existingSite) {
          existingSite.qty += item.stockQuantity;
        } else {
          acc[item.sku].stockBreakdown.push({ site: siteName, qty: item.stockQuantity });
        }
      }
    }
    return Object.values(acc).map((item: any) => ({
      ...item,
      stockLocation: Array.from(item.stockLocations).join(' | ')
    }));
  }, [haloStock]);

  const groupedStock = useMemo(() => {
    const groups: Record<string, any[]> = {};
    if (!aggregatedHaloStock) return groups;
    for (const item of aggregatedHaloStock) {
      const type = item.itemType || 'Other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(item);
    }
    return groups;
  }, [aggregatedHaloStock]);

  const derivedBackorders = useMemo(() => {
    const backorders: { itemName: string; missingQty: number; sku: string }[] = [];
    Object.entries(cart).forEach(([sku, qty]) => {
      if (qty > 0) {
        const stock = aggregatedHaloStock?.find(s => s.sku === sku);
        const stockQty = stock ? stock.stockQuantity : 0;
        if (qty > stockQty) {
          const missingQty = qty - stockQty;
          const matchingProduct = catalogProducts?.find(p => p.productId === sku || p.code === sku);
          backorders.push({
            itemName: stock?.item || matchingProduct?.name || sku,
            missingQty,
            sku
          });
        }
      }
    });
    return backorders;
  }, [cart, aggregatedHaloStock, catalogProducts]);

  const handleBundleChange = (property: { id: string; name: string } | null) => {
    const bundleId = property?.id;
    if (!bundleId) {
      setSelectedBundleId(null);
      return;
    }
    
    setSelectedBundleId(bundleId);
    const bundle = bundles?.find(b => b.id === bundleId);
    if (!bundle) return;

    const newCart: Record<string, number> = {};
    const requiredItems = [...(bundle.bundledItems || [])];

    requiredItems.forEach(item => {
      let remainingQuantity = item.count;
      const bundleCategory = categories?.find(c => c.id === item.catalogItem);
      const bundleProduct = catalogProducts?.find(p => p.productId === item.catalogItem || p.id === item.catalogItem);
      
      const matchingStock = (aggregatedHaloStock || []).filter((s: any) => {
        if (bundleCategory) return s.itemType?.toLowerCase() === bundleCategory.name?.toLowerCase();
        if (bundleProduct) return s.sku === bundleProduct.code || s.sku === bundleProduct.productId;
        return false;
      });
      
      // Try to fulfill with in-stock items first
      for (const stock of matchingStock) {
        if (remainingQuantity <= 0) break;
        const currentCartQty = newCart[stock.sku] || 0;
        const availableToAdd = stock.stockQuantity - currentCartQty;
        
        if (availableToAdd > 0) {
          const qtyToAdd = Math.min(availableToAdd, remainingQuantity);
          newCart[stock.sku] = currentCartQty + qtyToAdd;
          remainingQuantity -= qtyToAdd;
        }
      }

      // If still remaining, force add to the first matching stock item (backorder)
      if (remainingQuantity > 0 && matchingStock.length > 0) {
        const stock = matchingStock[0];
        const currentCartQty = newCart[stock.sku] || 0;
        newCart[stock.sku] = currentCartQty + remainingQuantity;
        remainingQuantity = 0;
      }
    });

    if (simulateDeploymentWeight(newCart) > getMaxAllowedRefreshWeight()) {
      toast.error('This will exceed a single courier consignment limit, please contact your account manager for a bespoke quotation', {
        className: '!border-2 !border-secondary-600'
      });
      setSelectedBundleId(null);
      return;
    }

    setCart(newCart);
  };

  const handleCartChange = (sku: string, qty: number) => {
    const proposed = { ...cart, [sku]: qty };
    if (simulateDeploymentWeight(proposed) > getMaxAllowedRefreshWeight()) {
      toast.error('This will exceed a single courier consignment limit, please contact your account manager for a bespoke quotation', {
        className: '!border-2 !border-secondary-600'
      });
      return;
    }
    setCart(proposed);
  };

  // -- Collection Schema & Form --
  const schema = useMemo(() => {
    const shape: any = {};
    shape.fullName = z.string().min(1, 'Required');
    shape.workEmail = z.string().email('Invalid email').optional().or(z.literal('na')).or(z.literal(''));
    shape.personalEmail = z.string().optional();
    shape.mobileNumber = z.string().optional();
    shape.noCorporateEmail = z.boolean().default(false);

    triggerTypes.forEach((t) => {
      shape[t] = z.number().min(0).default(0);
    });

    shape.assets = z.record(
      z.string(),
      z.array(
        z.object({
          makeModel: z.string().min(1, 'Make/Model is required'),
          serialNumber: z.string().min(1, 'Serial number is required'),
          assetTag: z.string().optional()
        })
      )
    ).default({});

    return z.object(shape).catchall(z.any()).superRefine((data, ctx) => {
      let anyHardwareSelected = false;
      triggerTypes.forEach((t) => {
        if ((data[t] as number) > 0) anyHardwareSelected = true;
      });

      const seenSns = new Set<string>();
      
      triggerTypes.forEach((t) => {
        const qty = (data[t] as number) || 0;
        if (qty > 0 && isTypeSerialized(t)) {
          const catAssets = data.assets?.[t] || [];
          for (let i = 0; i < qty; i++) {
            const asset = catAssets[i];
            if (!asset) continue;
            
            const sn = asset.serialNumber;
            if (!sn || typeof sn !== 'string' || sn.trim() === '') {
              // Validated by object schema
            } else {
              const normalizedSn = sn.toLowerCase().trim();
              if (seenSns.has(normalizedSn)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Serial numbers must be unique', path: ['assets', t, i, 'serialNumber'] });
              }
              seenSns.add(normalizedSn);
            }
          }
        }
      });
    });
  }, [triggerTypes]);

  type FormFields = z.infer<typeof schema>;

  const methods = useForm<FormFields>({
    resolver: zodResolver(schema),
    defaultValues: { macroAction: null }
  });

  useEffect(() => {
    if (triggerTypes.length > 0) {
      const defaults: any = { macroAction: null };
      triggerTypes.forEach(t => {
        defaults[t] = 0;
      });
      defaults.assets = {};
      defaults.fullName = '';
      defaults.workEmail = '';
      defaults.personalEmail = '';
      defaults.mobileNumber = '';
      defaults.noCorporateEmail = false;
      
      defaults.deliveryContactName = '';
      defaults.deliveryContactEmail = '';
      defaults.deliveryContactMobile = '';
      defaults.isAsapExpedite = false;
      
      defaults.address = {};
      defaults.targetDate = new Date().toISOString();
      
      methods.reset(defaults);
    }
  }, [triggerTypes, methods]);

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const modalProps = useModal();
  const [checkoutPayload, setCheckoutPayload] = useState<any>(null);
  const [selectedUserEquipment, setSelectedUserEquipment] = useState<HaloEquipmentDto[]>([]);
  const [currentStep, setCurrentStep] = useState(1);

  const handleNextStep = async () => {
    let fieldsToValidate: any[] = [];
    if (currentStep === 1) {
      fieldsToValidate = ['fullName', 'workEmail', 'personalEmail', 'mobileNumber'];
      triggerTypes.forEach(t => {
        fieldsToValidate.push(t);
        fieldsToValidate.push(`assets.${t}`);
      });
    } else if (currentStep === 2) {
      const hardwareSelections: Record<string, number> = {};
      const data = methods.getValues();
      triggerTypes.forEach((t) => {
        const quantity = data[t] as number | undefined;
        if (quantity && quantity > 0) {
          hardwareSelections[t] = quantity;
        }
      });
      const cartHasItems = Object.values(cart).some(q => q > 0);
      const collectionHasItems = Object.values(hardwareSelections).some(q => q > 0);
      if (!cartHasItems && !collectionHasItems) {
        setErrorMessage('You must select at least one item to collect or one item to deploy.');
        return;
      }
      setErrorMessage('');
    }
    
    const isValid = await methods.trigger(fieldsToValidate);
    if (isValid) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  const formValues = methods.watch();


  const handleTriggerChange = (t: string, qty: number, field: any) => {
    const currentFormValues = methods.getValues();
    const currentQty = currentFormValues[t] as number || 0;
    
    if (isTypeSerialized(t)) {
      const currentAssets = (currentFormValues.assets as Record<string, any[]>)?.[t] || [];
      const newAssets = [...currentAssets];
      if (qty > currentQty) {
        for (let i = currentQty; i < qty; i++) {
          newAssets.push({ makeModel: '', serialNumber: '', assetTag: '' });
        }
      } else if (qty < currentQty) {
        newAssets.splice(qty);
      }
      methods.setValue(`assets.${t}` as any, newAssets, { shouldValidate: true, shouldDirty: true });
    }

    field.onChange(qty);
  };

  const getMaxAllowedRefreshWeight = () => {
    const tierCards = availableRefreshCards.filter(c => c.propertyBag.maxChargeableWeightKg !== undefined && !c.propertyBag.isExclusiveOverride).sort((a, b) => (a.propertyBag.maxChargeableWeightKg || 0) - (b.propertyBag.maxChargeableWeightKg || 0));
    if (tierCards.length > 0) {
      return tierCards[tierCards.length - 1].propertyBag.maxChargeableWeightKg!;
    }
    return Infinity;
  };

  const simulateDeploymentWeight = (proposedCart: Record<string, number>) => {
    let totalDeadWeight = 0;
    let totalVolumetricWeight = 0;


    Object.entries(proposedCart).forEach(([sku, qty]) => {
      if (qty <= 0) return;
      const stockItem = aggregatedHaloStock?.find((s: any) => s.sku === sku);
      if (!stockItem) return;

      const category = categories?.find(c => {
        let cName = c.name.toLowerCase().trim();
        if (cName.endsWith('s')) cName = cName.slice(0, -1);
        let nt = (stockItem.itemType || '').toLowerCase().trim();
        if (nt.endsWith('s')) nt = nt.slice(0, -1);
        return cName === nt;
      });

      if (category) {
        if (resolveDeployOverrideRateCardId(category, company?.region?.id)) return; // Skip overrides!
        for(let i=0; i<qty; i++) {
          totalDeadWeight += (category.logisticsConfig?.deploy?.averageWeightKg || 0);
          const baseVolWeight = (category.logisticsConfig?.deploy?.dimensionalWeightKg || 0);
          const packingEff = category.logisticsConfig?.deploy?.packingEfficiencyPercent || 100;
          const packedVolWeight = baseVolWeight / (packingEff / 100);
          totalVolumetricWeight += packedVolWeight;
        }
      }
    });

    return Math.max(totalDeadWeight, totalVolumetricWeight);
  };

interface InferredService {
  rateCard?: any;
  name: string;
  itemType: string;
  quantity: number;
  price?: number;
  total?: number;
  isInventoryItem?: boolean;
  hasLifecyclePack?: boolean;
  associatedPhysicalItems?: any[];
  listSection?: 'admin' | 'logistics' | 'processing';
}

  const { isSandboxMode, setTelemetry } = useSandbox();

  // -- Unified Inferred Services --
  const { inferredServices, runningTotal, telemetryData } = useMemo(() => {
    let total = 0;
    const services: InferredService[] = [];
    const telemetryData = {
      collection: { totalDeadWeight: 0, totalVolumetricWeight: 0, chargeableWeight: 0, overriddenIndices: [] as number[], itemLog: [] as any[] },
      deployment: { totalDeadWeight: 0, totalVolumetricWeight: 0, chargeableWeight: 0, overriddenSkus: [] as string[], itemLog: [] as any[] }
    };

    // --- ZERO-COST HARDWARE INJECTION ---
    const pendingItems: Array<{ type: string, hasLifecyclePack: boolean, isCollectionItem: boolean, rawItem?: any, suppressSerialInput?: boolean }> = [];
    triggerTypes.forEach((t) => {
      const qty = (formValues[t] as number) || 0;
      if (qty > 0) {
        const matchedEquipment = selectedUserEquipment.filter(item => {
          let normalizedType = item.type?.toLowerCase().trim() || '';
          if (normalizedType.endsWith('s')) normalizedType = normalizedType.slice(0, -1);
          let nt = t.toLowerCase().trim();
          if (nt.endsWith('s')) nt = nt.slice(0, -1);
          return nt === normalizedType;
        });
        
        let lifecyclePackCount = matchedEquipment.filter(e => e.hasLifecyclePack).length;

        for (let i = 0; i < qty; i++) {
          if (lifecyclePackCount > 0) {
            pendingItems.push({ type: t, hasLifecyclePack: true, isCollectionItem: true });
            lifecyclePackCount--;
          } else {
            pendingItems.push({ type: t, hasLifecyclePack: false, isCollectionItem: true });
          }
        }
      }
    });

    // Zero-Cost Injection (Deployment)
    // Removed to prevent UI spam - deployed hardware will only render nested under associatedPhysicalItems

    const hasCollectionItems = pendingItems.length > 0;
    const hasDeploymentItems = Object.values(cart).some(qty => qty > 0);
    if (!hasCollectionItems && !hasDeploymentItems) return { inferredServices: services, runningTotal: total, telemetryData };

    // --- ROOT PASS (Base Pack) ---
    const techRefreshBasePack = rateCards?.items?.find(c => c.propertyBag.basePackForRequestType === 'TechRefresh');
    if (techRefreshBasePack) {
      const rawPrice = techRefreshBasePack.price;
      const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
      const price = priceInPence / 100;
      services.push({ 
        listSection: 'admin',
        rateCard: techRefreshBasePack, 
        name: techRefreshBasePack.name || 'Tech Refresh Base Pack',
        itemType: 'Service',
        quantity: 1, 
        price,
        total: price,
        isInventoryItem: false 
      });
      total += price;
    }

    // --- POOL B: COLLECTION ---
    if (hasCollectionItems) {
      // Pass 2.5: Override Extract (Collection)
      const overriddenIndexes = new Set<number>();
      pendingItems.forEach((item, index) => {
        const category = categories?.find(c => {
          let cName = c.name.toLowerCase().trim();
          if (cName.endsWith('s')) cName = cName.slice(0, -1);
          let nt = item.type.toLowerCase().trim();
          if (nt.endsWith('s')) nt = nt.slice(0, -1);
          return cName === nt;
        });

        const colOverrideId = resolveReturnOverrideRateCardId(category, company?.region?.id);
        if (colOverrideId) {
          overriddenIndexes.add(index);
          item.suppressSerialInput = true;
        }
      });

      // Pass 2: Per-Item Handling (Collection)
      const collectionProcessingGroups: Record<string, { items: typeof pendingItems }> = {};
      const collectionGenericItems: typeof pendingItems = [];
      let collectionGenericQty = 0;

      pendingItems.forEach(item => {
        const category = categories?.find(c => {
          let cName = c.name.toLowerCase().trim();
          if (cName.endsWith('s')) cName = cName.slice(0, -1);
          let nt = item.type.toLowerCase().trim();
          if (nt.endsWith('s')) nt = nt.slice(0, -1);
          return cName === nt;
        });

        const itemProcessingRateCardId = resolveReturnProcessingRateCardId(category, company?.region?.id);

        if (itemProcessingRateCardId) {
          if (!collectionProcessingGroups[itemProcessingRateCardId]) {
            collectionProcessingGroups[itemProcessingRateCardId] = { items: [] };
          }
          collectionProcessingGroups[itemProcessingRateCardId].items.push(item);
        } else {
          collectionGenericItems.push(item);
          collectionGenericQty += 1;
        }
      });

      Object.entries(collectionProcessingGroups).forEach(([rateCardId, group]) => {
        const specificCard = availableCollectionCards.find(r => r.id === rateCardId);
        if (specificCard && group.items.length > 0) {
          const rawPrice = specificCard.price;
          const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
          const price = priceInPence / 100;
          const qty = group.items.length;

          services.push({
            listSection: 'processing',
            rateCard: specificCard,
            name: specificCard.name,
            itemType: 'Service',
            quantity: qty,
            hasLifecyclePack: group.items.some(i => i.hasLifecyclePack),
            associatedPhysicalItems: group.items,
            isInventoryItem: false,
            price,
            total: price * qty
          });
          total += price * qty;
        }
      });

      if (collectionGenericQty > 0) {
        const handlingCard = availableCollectionCards.find(c => c.propertyBag.itemRateForRequestType === 'OffboardUser' || c.propertyBag.itemRateForRequestType === 'Offboard');
        if (handlingCard) {
          const lifecyclePackItems = collectionGenericItems.filter(i => i.hasLifecyclePack);
          const standardItems = collectionGenericItems.filter(i => !i.hasLifecyclePack);
          const rawPrice = handlingCard.price;
          const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
          const price = priceInPence / 100;

          if (lifecyclePackItems.length > 0) {
            services.push({ 
              listSection: 'processing',
              rateCard: handlingCard, 
              name: handlingCard.name || 'Collection Handling',
              itemType: 'Service',
              quantity: lifecyclePackItems.length, 
              hasLifecyclePack: true,
              associatedPhysicalItems: lifecyclePackItems,
              isInventoryItem: false,
              price,
              total: price * lifecyclePackItems.length
            });
            total += price * lifecyclePackItems.length;
          }
          if (standardItems.length > 0) {
            services.push({ 
              listSection: 'processing',
              rateCard: handlingCard, 
              name: handlingCard.name || 'Collection Handling',
              itemType: 'Service',
              quantity: standardItems.length, 
              hasLifecyclePack: false,
              associatedPhysicalItems: standardItems,
              isInventoryItem: false,
              price,
              total: price * standardItems.length
            });
            total += price * standardItems.length;
          }
        }
      }

      // Pass 3: Item Telemetry (Collection)
      const collectionItemLog: any[] = [];

      pendingItems.forEach((item, index) => {
        const isOverridden = overriddenIndexes.has(index);
        const category = categories?.find(c => {
          let cName = c.name.toLowerCase().trim();
          if (cName.endsWith('s')) cName = cName.slice(0, -1);
          let nt = item.type.toLowerCase().trim();
          if (nt.endsWith('s')) nt = nt.slice(0, -1);
          return cName === nt;
        });

        collectionItemLog.push({
          name: category?.name || item.type,
          quantity: 1,
          deadWeightKg: 0,
          baseVolumetricKg: 0,
          packingEfficiency: 100,
          packedVolumetricKg: 0,
          isOverridden
        });
      });

      telemetryData.collection = {
        totalDeadWeight: 0,
        totalVolumetricWeight: 0,
        chargeableWeight: 0,
        overriddenIndices: Array.from(overriddenIndexes),
        itemLog: collectionItemLog
      };
    }

    // --- POOL A: DEPLOYMENT ---
    if (hasDeploymentItems) {
      // Pass 2.5: Override Extract (Deployment)
      const overriddenDeploySkus = new Set<string>();

      Object.entries(cart).forEach(([sku, quantity]) => {
        if (quantity <= 0) return;
        const stockItem = aggregatedHaloStock?.find((s: any) => s.sku === sku);
        if (!stockItem) return;
        const itemType = stockItem.itemType || '';
        
        const category = categories?.find(c => {
          let cName = c.name.toLowerCase().trim();
          if (cName.endsWith('s')) cName = cName.slice(0, -1);
          let nt = itemType.toLowerCase().trim();
          if (nt.endsWith('s')) nt = nt.slice(0, -1);
          return cName === nt;
        });

        const depOverrideId = resolveDeployOverrideRateCardId(category, company?.region?.id);
        if (depOverrideId) {
          const overrideCard = availableDeploymentCards.find(r => r.id === depOverrideId);
          if (overrideCard) {
            overriddenDeploySkus.add(sku);
            
            const overridePhysicalItems = [];
            for(let i=0; i<quantity; i++) {
              overridePhysicalItems.push({
                type: stockItem.item,
                hasLifecyclePack: false,
                isCollectionItem: false,
                suppressSerialInput: true
              });
            }

            const rawPrice = overrideCard.price;
            const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
            const price = priceInPence / 100;
            services.push({
              listSection: 'logistics',
              rateCard: overrideCard,
              name: overrideCard.name || 'Deployment Crate Fee',
              itemType: 'Service',
              quantity,
              associatedPhysicalItems: overridePhysicalItems,
              isInventoryItem: false,
              price,
              total: price * quantity
            });
            total += (price * quantity);
          }
        }
      });

      // Pass 2: Per-Item Handling (Deployment)
      const deployProcessingGroups: Record<string, { items: any[], totalQty: number }> = {};
      const deployGenericItems: any[] = [];
      let deployGenericQty = 0;

      Object.entries(cart).forEach(([sku, quantity]) => {
        if (quantity <= 0) return;
        const stockItem = aggregatedHaloStock?.find((s: any) => s.sku === sku);
        if (!stockItem) return;

        const category = categories?.find(c => {
          let cName = c.name.toLowerCase().trim();
          if (cName.endsWith('s')) cName = cName.slice(0, -1);
          let nt = (stockItem.itemType || '').toLowerCase().trim();
          if (nt.endsWith('s')) nt = nt.slice(0, -1);
          return cName === nt;
        });

        const itemProcessingRateCardId = resolveDeployProcessingRateCardId(category, company?.region?.id);

        const physicalItems = Array(quantity).fill(null).map(() => ({
          type: stockItem.item,
          hasLifecyclePack: false,
          isCollectionItem: false
        }));

        if (itemProcessingRateCardId) {
          if (!deployProcessingGroups[itemProcessingRateCardId]) {
            deployProcessingGroups[itemProcessingRateCardId] = { items: [], totalQty: 0 };
          }
          deployProcessingGroups[itemProcessingRateCardId].items.push(...physicalItems);
          deployProcessingGroups[itemProcessingRateCardId].totalQty += quantity;
        } else {
          deployGenericItems.push(...physicalItems);
          deployGenericQty += quantity;
        }
      });

      Object.entries(deployProcessingGroups).forEach(([rateCardId, group]) => {
        const specificCard = availableDeploymentCards.find(r => r.id === rateCardId);
        if (specificCard && group.totalQty > 0) {
          const rawPrice = specificCard.price;
          const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
          const price = priceInPence / 100;

          services.push({
            listSection: 'processing',
            rateCard: specificCard,
            name: specificCard.name,
            itemType: 'Service',
            quantity: group.totalQty,
            associatedPhysicalItems: group.items,
            isInventoryItem: false,
            price,
            total: price * group.totalQty
          });
          total += price * group.totalQty;
        }
      });

      if (deployGenericQty > 0) {
        const deployHandlingCard = availableDeploymentCards.find(c => c.propertyBag.itemRateForRequestType === 'DeployInventory');
        if (deployHandlingCard) {
          const rawPrice = deployHandlingCard.price;
          const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
          const price = priceInPence / 100;

          services.push({
            listSection: 'processing',
            rateCard: deployHandlingCard,
            name: deployHandlingCard.name || 'Deployment Handling',
            itemType: 'Service',
            quantity: deployGenericQty,
            associatedPhysicalItems: deployGenericItems,
            isInventoryItem: false,
            price,
            total: price * deployGenericQty
          });
          total += price * deployGenericQty;
        }
      }

      let deployDeadWeight = 0;
      let deployVolumetricWeight = 0;
      const deploymentItemLog: any[] = [];

      Object.entries(cart).forEach(([sku, quantity]) => {
        if (quantity <= 0) return;
        const isOverridden = overriddenDeploySkus.has(sku);
        const stockItem = aggregatedHaloStock?.find((s: any) => s.sku === sku);
        if (!stockItem) return;

        const category = categories?.find(c => {
          let cName = c.name.toLowerCase().trim();
          if (cName.endsWith('s')) cName = cName.slice(0, -1);
          let nt = (stockItem.itemType || '').toLowerCase().trim();
          if (nt.endsWith('s')) nt = nt.slice(0, -1);
          return cName === nt;
        });

        let deadWt = 0;
        let baseVol = 0;
        let packEff = 100;
        let packedVol = 0;

        if (category) {
          deadWt = (category.logisticsConfig?.deploy?.averageWeightKg || 0);
          baseVol = (category.logisticsConfig?.deploy?.dimensionalWeightKg || 0);
          packEff = (category.logisticsConfig?.deploy?.packingEfficiencyPercent || 100);
          packedVol = baseVol / (packEff / 100);

          if (!isOverridden) {
            deployDeadWeight += (deadWt * quantity);
            deployVolumetricWeight += (packedVol * quantity);
          }
        }

        deploymentItemLog.push({
          name: stockItem.item || sku,
          quantity,
          deadWeightKg: deadWt,
          baseVolumetricKg: baseVol,
          packingEfficiency: packEff,
          packedVolumetricKg: packedVol,
          isOverridden
        });
      });

      const deployChargeableWeight = Math.max(deployDeadWeight, deployVolumetricWeight);
      telemetryData.deployment = {
        totalDeadWeight: deployDeadWeight,
        totalVolumetricWeight: deployVolumetricWeight,
        chargeableWeight: deployChargeableWeight,
        overriddenSkus: Array.from(overriddenDeploySkus),
        itemLog: deploymentItemLog
      };

      if (deployChargeableWeight > 0) {
        const tierCards = availableRefreshCards.filter(c => c.propertyBag.maxChargeableWeightKg !== undefined && !c.propertyBag.isExclusiveOverride)
          .sort((a, b) => a.propertyBag.maxChargeableWeightKg! - b.propertyBag.maxChargeableWeightKg!);
        
        if (tierCards && tierCards.length > 0) {
          const matchedTier = tierCards.find(c => deployChargeableWeight <= c.propertyBag.maxChargeableWeightKg!) || tierCards[tierCards.length - 1];
          const rawPrice = matchedTier.price;
          const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
          const price = priceInPence / 100;
          services.push({
            listSection: 'logistics',
            rateCard: matchedTier,
            name: matchedTier.name || 'Refresh Shipping Tier',
            itemType: 'Service',
            quantity: 1,
            isInventoryItem: false,
            price,
            total: price
          });
          total += price;
        }
      }
    }
    
    return { inferredServices: services, runningTotal: total, telemetryData };
  }, [availableCollectionCards, availableRefreshCards, availableDeploymentCards, rateCards, formValues, triggerTypes, cart, aggregatedHaloStock, selectedUserEquipment, categories]);

  const telemetryString = JSON.stringify(telemetryData);
  useEffect(() => {
    if (isSandboxMode && telemetryString) {
      setTelemetry(JSON.parse(telemetryString));
    }
  }, [telemetryString, isSandboxMode, setTelemetry]);

  const handleMacroSelect = (selectedUser: any | null) => {
    // Reset all triggers and dynamic fields to zero out basket before applying macro
    categories.forEach((cat) => {
      methods.setValue(cat.name as any, 0, { shouldValidate: true, shouldDirty: true });
    });
    
    methods.setValue('assets' as any, {}, { shouldValidate: true, shouldDirty: true });

    if (!selectedUser) {
      setSelectedUserEquipment([]);
      return;
    }

    if (equipmentPayload) {
      let userEquipment = equipmentPayload.filter((e: HaloEquipmentDto) => e.user === selectedUser.rawValue);
      
      const primaryTypes = ['laptop', 'desktop', 'pc', 'macbook', 'phone', 'tablet'];
      userEquipment = userEquipment.sort((a: HaloEquipmentDto, b: HaloEquipmentDto) => {
        const aIsPrimary = primaryTypes.some(pt => a.type.toLowerCase().includes(pt)) ? 1 : 0;
        const bIsPrimary = primaryTypes.some(pt => b.type.toLowerCase().includes(pt)) ? 1 : 0;
        return bIsPrimary - aIsPrimary;
      });

      setSelectedUserEquipment(userEquipment);
      
      const unmapped: string[] = [];

      for (const item of userEquipment) {
        let normalizedType = item.type?.toLowerCase().trim() || '';
        if (normalizedType.endsWith('s')) normalizedType = normalizedType.slice(0, -1);
        
        let matchFound = false;

        for (const category of categories || []) {
          let cName = category.name.toLowerCase().trim();
          if (cName.endsWith('s')) cName = cName.slice(0, -1);
          
          if (cName === normalizedType) {
            matchFound = true;
            const t = category.name;
            const currentStepperValue = Number(methods.getValues(t as any)) || 0;
            methods.setValue(t as any, currentStepperValue + 1, { shouldValidate: true, shouldDirty: true });
            
            if (category.isSerialized) {
              const currentAssets = (methods.getValues('assets' as any) as Record<string, any[]>)?.[t] || [];
              const newAssets = [...currentAssets];
              newAssets.push({ makeModel: item.sku || item.type, serialNumber: item.serial || '', assetTag: '' });
              methods.setValue(`assets.${t}` as any, newAssets, { shouldValidate: true, shouldDirty: true });
            }

            break;
          }
        }
        
        if (!matchFound) {
          unmapped.push(item.type || 'Unknown Item');
        }
      }

      if (unmapped.length > 0) {
        toast(`The following assigned equipment has no matching Category: ${unmapped.join(', ')}`, {
          style: { backgroundColor: '#fff3cd', color: '#856404', borderColor: '#ffeeba' }
        });
      }
    }
  };

  const onSubmit = async (data: any) => {
    let missingSn = false;
    let duplicateSn = false;
    const seenSns = new Set<string>();

    triggerTypes.forEach((t) => {
      const qty = (data[t] as number) || 0;
      if (qty > 0 && isTypeSerialized(t)) {
        const catAssets = data.assets?.[t] || [];
        for (let i = 0; i < qty; i++) {
          const sn = catAssets[i]?.serialNumber;
          if (!sn || typeof sn !== 'string' || sn.trim() === '') {
            missingSn = true;
          } else {
            const normalizedSn = sn.trim().toLowerCase();
            if (seenSns.has(normalizedSn)) duplicateSn = true;
            seenSns.add(normalizedSn);
          }
        }
      }
    });

    if (missingSn) {
      setErrorMessage('Please provide all required serial numbers for collection.');
      return;
    }

    if (duplicateSn) {
      setErrorMessage('Duplicate serial numbers detected for collection.');
      return;
    }

    const hardwareSelections: Record<string, number> = {};
    triggerTypes.forEach((t, index) => {
      const quantity = data[t] as number | undefined;
      if (quantity && quantity > 0) {
        hardwareSelections[t] = quantity;
      }
    });
    
    const cartHasItems = Object.values(cart).some(q => q > 0);
    const collectionHasItems = Object.values(hardwareSelections).some(q => q > 0);

    if (!cartHasItems && !collectionHasItems) {
        setErrorMessage('You must select at least one item to collect or one item to deploy to submit a Tech Refresh request.');
        return;
    }

    setLoading(true);
    setErrorMessage('');

    const devicesToBeCollected: string[] = [];
    
    triggerTypes.forEach((t) => {
      const qty = (data[t] as number) || 0;
      if (qty > 0) {
        if (isTypeSerialized(t)) {
          const catAssets = data.assets?.[t] || [];
          for (let i = 0; i < qty; i++) {
            const asset = catAssets[i];
            if (asset) {
              const tag = asset.assetTag ? asset.assetTag.trim() : '';
              devicesToBeCollected.push(`${t}: ${asset.makeModel}, Serial: ${asset.serialNumber}, Tag: ${tag || 'none'}`);
            }
          }
        } else {
          devicesToBeCollected.push(`${qty}x ${t}`);
        }
      }
    });

    const { companyId: cleanCompanyId, partnerId: cleanPartnerId, ...cleanAddress } = data.address as any;
    
    const finalCustomerReference = data.customerReference?.trim() || data.fullName || 'No Reference Provided';

    const finalPayload = {
      requestContext: {
        requestType: 'TechRefresh',
        requestedBy: user?.email || 'Unknown',
        mspId: partnerId,
        mspName: partner?.name || '',
        customerId: companyId,
        customerName: company?.name || '',
        raisedDateTime: new Date().toISOString(),
        customerReference: finalCustomerReference,
        submissionSource: 'DaaS Portal UI',
        haloClientId: company?.haloClientId || null,
        haloGeneralUserId: company?.haloGeneralUserId || null
      },
      financialContext: {
        totalEstimatedCost: Math.round(runningTotal * 100),
        totalCost: Math.round(runningTotal * 100),
        inferredServices: inferredServices.map((s: any) => ({ 
          rateCardId: s.rateCard?.id, 
          name: s.name || s.rateCard?.name,
          quantity: s.quantity,
          price: Math.round((s.rateCard?.price?.price || 0) * 100)
        }))
      },
      userContext: {
        userName: data.fullName,
        userEmail: data.workEmail,
        personalEmail: data.personalEmail,
        userMobile: data.mobileNumber,
        noCorporateEmail: data.noCorporateEmail
      },
      deployment: {
        isRequired: true,
        logistics: {
          schedule: {
            requestedDate: new Date(data.targetDate).toISOString(),
            isAsapExpedite: data.isAsapExpedite
          },
          address: { ...cleanAddress, name: 'Tech Refresh Address' },
          siteContact: {
            contactName: data.deliveryContactName,
            contactEmail: data.deliveryContactEmail,
            contactMobile: data.deliveryContactMobile,
          },
          notes: data.notes
        },
        items: Object.entries(cart).map(([sku, quantity]) => {
          const stockItem = aggregatedHaloStock?.find((s: any) => s.sku === sku);
          const categoryName = stockItem?.itemType || stockItem?.name || 'Device';

          return {
            category: categoryName,
            sku: sku,
            serialNumber: null,
            assetTag: null,
            quantity: quantity as number,
            warranty: { expiryDate: null, hasAdrWarranty: false, lifecyclePack: "No" }
          };
        })
      },
      collection: {
        isRequired: true,
        logistics: {
          schedule: {
            requestedDate: new Date(data.targetDate).toISOString(),
            isAsapExpedite: data.isAsapExpedite
          },
          address: { ...cleanAddress, name: 'Tech Refresh Address' },
          siteContact: {
            contactName: data.deliveryContactName,
            contactEmail: data.deliveryContactEmail,
            contactMobile: data.deliveryContactMobile,
          },
          notes: data.notes
        },
        items: [
          ...devicesToBeCollected.map((d: string) => {
            const parsed = parseCollectedItemString(d, 1);
            const hasPack = inferredServices.some((s: any) => (s.name === d || (s.rateCard && s.rateCard.name === d)) && s.hasLifecyclePack);
            return {
              ...parsed,
              warranty: { expiryDate: null, hasAdrWarranty: false, lifecyclePack: hasPack ? "Yes" : "No" }
            };
          })
        ]
      }
    };

    const deployedHardware = Object.entries(cart)
      .filter(([_, qty]) => qty > 0)
      .map(([sku, qty]) => {
        const stockItem = aggregatedHaloStock?.find((s: any) => s.sku === sku);
        return {
          name: stockItem?.item || sku,
          quantity: qty as number,
          sku: sku
        };
      });

    setCheckoutPayload({
      requestedBy: user?.email || 'Unknown',
      requestType: 'TechRefresh',
      customerReference: finalCustomerReference,
      userContext: {
        fullName: data.fullName,
        workEmail: data.workEmail,
        personalEmail: data.personalEmail,
        mobileNumber: data.mobileNumber,
        noCorporateEmail: data.noCorporateEmail
      },
      payload: finalPayload,
      runningTotal,
      devicesToBeCollected,
      deployedHardware,
      inferredServices,
      cart
    });
    setLoading(false);
    modalProps.open();
  };

  const resetForm = useCallback(() => {
    const defaults: any = {
      macroAction: null,
      assets: {},
      fullName: '',
      workEmail: '',
      address: {},
      targetDate: new Date().toISOString()
    };
    categories?.forEach((cat) => {
      defaults[cat.name] = 0;
    });

    methods.reset(defaults);
    setSelectedUserEquipment([]);
    setCart({});
    setSelectedBundleId(null);
    setCurrentStep(1);
  }, [methods, categories]);

  const isDirty = methods.formState.isDirty || Object.values(cart).some(qty => qty > 0);
  useDirtyFormGuard(isDirty);

  // Unused legacy constants removed
  return (
    <>
      <UnsavedChanges 
        show={isDirty} 
        title="Abandon Request"
        message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
        confirmText="Yes, Abandon"
        confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
        cancelText="No, Continue"
        cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
      />
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between items-start w-full">
          <div className="flex flex-col gap-2 items-start">
            <h3 className="text-3xl font-bold">Tech Refresh</h3>
            <p className="font-medium">Collect existing hardware and provision new hardware.</p>
          </div>
          {isAdmin && (
            isSandboxMode ? (
              <button
                type="button"
                onClick={() => navigate(`/msps/${partnerId}/companies/${companyId}/management/techRefresh`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors border border-red-200 shadow-sm"
              >
                <span>🚪</span> Exit Sandbox
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate(`/admin/sandbox/partners/${partnerId}/companies/${companyId}/tech-refresh`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono text-gray-500 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors border border-gray-200 shadow-sm"
              >
                <span>⚙️</span> Debug in Sandbox
              </button>
            )
          )}
        </div>
      </BreadcrumbPageHeader>
      <Form methods={methods as any} onSubmit={onSubmit}>
        <div className="px-4">
          <nav aria-label="Progress">
            <ol role="list" className="divide-y divide-gray-300 rounded-md bg-gray-100 overflow-hidden md:flex md:divide-y-0">
              {['Hardware to Collect', 'Hardware to Deploy', 'Order Summary'].map((stepName, stepIdx) => (
                <li key={stepName} className="relative md:flex md:flex-1">
                  <div className={`group flex w-full h-full items-center ${
                    stepIdx === 0 
                      ? 'md:[clip-path:polygon(0_0,calc(100%-1rem)_0,100%_50%,calc(100%-1rem)_100%,0_100%)]' 
                      : stepIdx === 2
                        ? 'md:[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,1rem_50%)] md:-ml-4'
                        : 'md:[clip-path:polygon(0_0,calc(100%-1rem)_0,100%_50%,calc(100%-1rem)_100%,0_100%,1rem_50%)] md:-ml-4'
                  } ${currentStep === stepIdx + 1 ? 'bg-primary-700' : currentStep > stepIdx + 1 ? 'bg-primary-50' : 'bg-white'}`}>
                    <span className={`flex items-center py-2.5 text-sm font-semibold ${stepIdx !== 0 ? 'px-4 md:pl-8' : 'px-4'}`}>
                      <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2 ${currentStep > stepIdx + 1 ? 'border-primary-700 bg-primary-700' : currentStep === stepIdx + 1 ? 'border-white' : 'border-gray-300 bg-white'}`}>
                        {currentStep > stepIdx + 1 ? (
                          <svg className="h-4 w-4 text-white" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                            <path fillRule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <span className={`text-xs ${currentStep === stepIdx + 1 ? 'text-white' : 'text-gray-500'}`}>{stepIdx + 1}</span>
                        )}
                      </span>
                      <span className={`ml-4 text-sm font-semibold ${currentStep === stepIdx + 1 ? 'text-white' : currentStep > stepIdx + 1 ? 'text-gray-700' : 'text-gray-500'}`}>{stepName}</span>
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          </nav>
        </div>
        <ServiceRequestLayout
          title=""
          macroSlot={
            currentStep === 1 ? (
              <div className="bg-white p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
                <div className="w-full">
                  <SmartUserSelector 
                    methods={methods as any} 
                    uniqueUsers={uniqueUsers} 
                    onSelect={handleMacroSelect} 
                    nameField="fullName" 
                    corpEmailField="workEmail" 
                    personalEmailLabel="Personal Email"
                    personalEmailOptional={true}
                    mobileNumberField="mobileNumber"
                    mobileNumberLabel="Personal Mobile Number"
                    mobileNumberOptional={true}
                    tooltipText="Select a user to pull their currently assigned equipment for collection."
                  />
                </div>
                {selectedUserEquipment.length > 0 && (
                  <div className="mt-4 bg-gray-50 p-3 rounded-md border text-sm w-full">
                    <h4 className="font-semibold text-gray-700 mb-2">Assigned Equipment (Halo)</h4>
                    <ul className="space-y-1 text-gray-600">
                      {selectedUserEquipment.map((item, idx) => (
                        <li key={idx}>
                          &bull; {item.type} ({item.sku})
                          {item.serial ? ` - SN: ${item.serial}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : null
          }
          leftColumn={
              <div className="space-y-8 pb-32">
                
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-xl font-bold border-b pb-2">Hardware to Collect</h3>
                    <HardwareCollectionSection triggerTypes={triggerTypes} deviceTriggers={deviceTriggers} peripheralTriggers={peripheralTriggers} handleTriggerChange={handleTriggerChange} isTypeSerialized={isTypeSerialized} methods={methods} />
                  </div>
                )}
  
                {currentStep === 2 && (
                  <div className="space-y-4 pt-4">
                    <h3 className="text-xl font-bold border-b pb-2">Hardware to Deploy</h3>
                    
                    {derivedBackorders.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-md">
                        <h4 className="font-bold mb-2 flex items-center gap-2">
                          <FiAlertTriangle className="w-5 h-5" /> 
                          Missing stock items warning: will be on backorder
                        </h4>
                        <ul className="list-disc pl-6 text-sm">
                          {derivedBackorders.map((w, idx) => (
                            <li key={idx}>
                              {w.missingQty}x {w.itemName} <span className="opacity-75 text-xs ml-1">({w.sku})</span> required but out of stock
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
                      <h2 className="text-lg font-bold mb-2">Quick Deploy Bundle</h2>
                      <SelectInput
                        id="bundle-select"
                        options={bundleOptions}
                        value={bundleOptions.find(o => o.id === selectedBundleId) || null}
                        onChange={handleBundleChange}
                        placeholder="Select a bundle to autofill..."
                      />
                    </div>
  
                    <div className="flex flex-col gap-4 lg:gap-6">
                      {Object.entries(groupedStock).map(([itemType, items]) => (
                        <div key={itemType}>
                          <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">{itemType}</h4>
                          <div className="flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden">
                            {items.map((item, idx) => {
                              const currentQty = cart[item.sku] || 0;
                              const matchingCatalogProduct = catalogProducts?.find(
                                p => p.productId === item.sku || p.code === item.sku
                              );
                              const hasImage = !!matchingCatalogProduct?.imageUri?.id;
                              const fallbackCategory = categories?.find(c => c.name.toLowerCase() === (item.itemType || '').toLowerCase());
                              const iconKey = matchingCatalogProduct?.category?.description || fallbackCategory?.description || '';
  
                              return (
                                <div key={`${item.sku}-${idx}`} className="flex flex-row items-center justify-between p-2 lg:p-3 border-b border-gray-100 hover:bg-gray-50 last:border-b-0">
                                  <div className="flex flex-row items-center gap-4 flex-1 overflow-hidden">
                                    <div className="w-12 h-12 flex items-center justify-center shrink-0">
                                      {hasImage ? (
                                        <Picture image={matchingCatalogProduct.imageUri!} containerHeight={48} containerWidth={48} />
                                      ) : (
                                      <div className="w-8 h-8 text-gray-300 [&_svg]:w-full [&_svg]:h-full">
                                        {getCategoryIcon(iconKey)}
                                      </div>
                                      )}
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-sm font-semibold text-gray-900 line-clamp-1" title={item.item}>
                                        {item.item}
                                      </span>
                                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                                        <span>SKU: {item.sku}</span>
                                        <span>Loc: {item.stockBreakdown ? item.stockBreakdown.map((b: any) => `${b.site} (${b.qty})`).join(' | ') : (item.stockLocation || 'N/A')}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="ml-4 shrink-0 flex items-center gap-3">
                                    {currentQty > item.stockQuantity && (
                                      <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                                        Backorder: {currentQty - item.stockQuantity}
                                      </span>
                                    )}
                                    <span className="text-sm font-medium text-gray-700">Your Stock: {item.stockQuantity}</span>
                                    <Counter
                                      id={`counter-${item.sku}`}
                                      value={currentQty}
                                      min={0}
                                      onChange={(val) => handleCartChange(item.sku, val)}
                                      allowManualChange={false}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      
                      {(!aggregatedHaloStock || aggregatedHaloStock.length === 0) && (
                        <div className="py-8 text-center text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
                          No warehouse stock available to deploy.
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {currentStep === 3 && (
                  <div className="space-y-6 pt-4">
                    <h3 className="text-xl font-bold border-b pb-2">Order Summary</h3>
                    <div className="flex flex-col gap-6">
                      <div className="border border-gray-200 p-4 rounded bg-white">
                        <h4 className="text-lg font-bold mb-4">Hardware to Collect</h4>
                        <ul className="space-y-2">
                          {triggerTypes.filter(t => (methods.getValues(t as any) as number) > 0).flatMap((t, i) => {
                            const qty = methods.getValues(t as any) as number;
                            const matchedEquipment = selectedUserEquipment.filter(item => {
                              let normalizedType = item.type?.toLowerCase().trim() || '';
                              if (normalizedType.endsWith('s')) normalizedType = normalizedType.slice(0, -1);
                              let nt = t.toLowerCase().trim();
                              if (nt.endsWith('s')) nt = nt.slice(0, -1);
                              return nt === normalizedType;
                            });
                            
                            const assignedItems = matchedEquipment.slice(0, qty);
                            const manualCount = Math.max(0, qty - matchedEquipment.length);
                            
                            const nodes = [];
                            assignedItems.forEach((eq: any, eqIdx) => {
                              nodes.push(
                                <li key={`${eq.serial}-${eqIdx}`} className="text-sm text-gray-700 pb-2 border-b border-gray-100 last:border-0 last:pb-0">
                                  {t}: {eq.makeModel || eq.sku || 'Unknown'} (S/N: {eq.serial || 'N/A'})
                                </li>
                              );
                            });
                            
                            if (manualCount > 0) {
                              nodes.push(
                                <li key={`manual-${i}`} className="text-sm text-gray-700 pb-2 border-b border-gray-100 last:border-0 last:pb-0">
                                  {manualCount}x {t} (Manual Entry)
                                </li>
                              );
                            }
                            return nodes;
                          })}
                          {triggerTypes.filter(t => (methods.getValues(t as any) as number) > 0).length === 0 && (
                            <li className="text-sm text-gray-500 italic">No hardware selected for collection.</li>
                          )}
                        </ul>
                      </div>
                      <div className="border border-gray-200 p-4 rounded bg-white">
                        <h4 className="text-lg font-bold mb-4">Hardware to Deploy</h4>
                        <ul className="space-y-2">
                          {Object.entries(cart).filter(([_, qty]) => qty > 0).map(([sku, qty]) => {
                            const stockItem = aggregatedHaloStock?.find((s: any) => s.sku === sku);
                            return (
                              <li key={sku} className="text-sm text-gray-700 pb-2 border-b border-gray-100 last:border-0 last:pb-0">
                                <span className="font-medium">{qty}x</span> {stockItem?.item || sku}
                                

                              </li>
                            );
                          })}
                          {Object.values(cart).filter(qty => qty > 0).length === 0 && (
                            <li className="text-sm text-gray-500 italic">No new hardware configured for deployment.</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                <div className="sticky bottom-0 bg-white z-10 flex justify-between items-center py-4 border-t border-gray-200 mt-8">
                  <div className="flex gap-4">
                    {currentStep > 1 && (
                      <Button variant={ButtonVariant.Secondary} onClick={handlePrevStep} type="button">
                        Back
                      </Button>
                    )}
                    <Button
                      variant={ButtonVariant.Secondary}
                      onClick={resetForm}
                      type="button"
                    >
                      Clear Form
                    </Button>
                  </div>
                  
                  {currentStep < 3 && (
                    <Button 
                      key="next-btn" 
                      variant={ButtonVariant.Primary} 
                      onClick={(e) => {
                        e.preventDefault();
                        handleNextStep();
                      }} 
                      type="button"
                    >
                      Next Step
                    </Button>
                  )}
                  {currentStep === 3 && (
                    <Button key="submit-btn" variant={ButtonVariant.Primary} type="submit" disabled={loading}>
                      Continue
                    </Button>
                  )}
                </div>
              </div>
            }
          rightColumn={
            <div className="border p-4 rounded bg-gray-50 shadow-sm space-y-4">
              <h4 className="text-xl font-bold border-b pb-2">Basket</h4>
              
              {(() => {
                const serialCounters: Record<string, number> = {};
                if (inferredServices.length === 0) {
                  return <p className="text-sm text-gray-500 italic">No hardware selected</p>;
                }
                
                const adminFees = inferredServices.filter(s => s.listSection === 'admin');
                const logisticsItems = inferredServices.filter(s => s.listSection === 'logistics');
                const processingItems = inferredServices.filter(s => s.isInventoryItem === true || s.listSection === 'processing');

                const renderService = (service: any, idx: string | number) => {
                  if (service.isInventoryItem) {
                    return (
                      <div key={idx} className="p-3 bg-white border rounded">
                        <div className="flex justify-between items-start text-sm">
                          <div className="pr-4">
                            <span className="font-medium">{service.quantity}x</span> {service.name}
                          </div>
                          <div className="font-medium whitespace-nowrap italic text-gray-500">
                            
                          </div>
                        </div>
                        {service.hasLifecyclePack && (
                          <span className="italic text-green-600 text-xs block mt-1">
                            Lifecycle Pack Detected<br />Costs will be adjusted accordingly
                          </span>
                        )}
                      </div>
                    );
                  }

                  const associatedPhysicalItems = (service as any).associatedPhysicalItems as Array<{ type: string, hasLifecyclePack: boolean, isCollectionItem?: boolean, suppressSerialInput?: boolean }>;
                  const physicalTypeGroups: Record<string, { count: number, type: string, isCollectionItem: boolean, suppressSerialInput: boolean }> = {};
                  
                  if (associatedPhysicalItems) {
                    associatedPhysicalItems.forEach(item => {
                      if (!physicalTypeGroups[item.type]) physicalTypeGroups[item.type] = { count: 0, type: item.type, isCollectionItem: !!item.isCollectionItem, suppressSerialInput: !!item.suppressSerialInput };
                      physicalTypeGroups[item.type].count++;
                    });
                  }

                  return (
                    <div key={idx} className="p-3 bg-white border rounded">
                      <div className="flex justify-between items-start text-sm">
                        <div className="pr-4">
                          <span className="font-medium">{service.quantity}x</span> {service.name || service.rateCard?.name}
                        </div>
                        <div className="font-medium whitespace-nowrap">
                          {formatCurrency(service.total ?? (((service.rateCard?.price?.price || 0) / 100) * service.quantity), company?.region?.code)}
                        </div>
                      </div>
                      
                      {service.hasLifecyclePack && (
                        <span className="italic text-green-600 text-xs block mt-1">
                          Lifecycle Pack Detected<br />Costs will be adjusted accordingly
                        </span>
                      )}

                      {associatedPhysicalItems && Object.keys(physicalTypeGroups).length > 0 && (
                        <div className="mt-2 space-y-3 pt-2 border-t border-gray-100 pl-4 border-l-2 border-l-blue-200">
                          {Object.values(physicalTypeGroups).map((group, groupIdx) => {
                            return (
                              <div key={groupIdx} className="space-y-2">
                                <p className="text-sm font-medium text-gray-700">{group.count}x {group.type}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                    {adminFees.length > 0 && (
                      <div className="space-y-3">
                        <h5 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase">
                          Administration
                          <InfoTooltip id="admin-info" text={getAdminTooltipText(company?.name ?? 'Company')} place="left-start" className="!max-w-none" />
                        </h5>
                        {adminFees.map((service, idx) => renderService(service, `admin-${idx}`))}
                      </div>
                    )}
                    {logisticsItems.length > 0 && (
                      <div className="space-y-3 pt-4 border-t border-gray-200">
                        <h5 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase">
                          Logistics & Insurance
                          <InfoTooltip id="logistics-info" text={logisticsTooltipText} place="left" className="!max-w-none" />
                        </h5>
                        {logisticsItems.map((service, idx) => renderService(service, `logistics-${idx}`))}
                      </div>
                    )}
                    {processingItems.length > 0 && (
                      <div className="space-y-3 pt-4 border-t border-gray-200">
                        <h5 className="flex items-center gap-2 text-sm font-semibold text-gray-500 uppercase">
                          Processing
                          <InfoTooltip id="processing-info" text={processingTooltipText} place="left" className="!max-w-none" />
                        </h5>
                        {processingItems.map((service, idx) => renderService(service, `proc-${idx}`))}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="flex justify-between items-center pt-4 text-lg font-bold border-t border-gray-300 mt-4">
                <span>Grand Total</span>
                <span>{formatCurrency(runningTotal, company?.region?.code)}</span>
              </div>

              {errorMessage && (
                <div className="text-red-600 text-sm mt-4 bg-red-50 p-3 rounded border border-red-200">
                  {errorMessage}
                </div>
              )}
            </div>
          }
        />
      </Form>
      
      {checkoutPayload && (
        <TechRefreshModal
        {...modalProps}
        mspId={partnerId}
        customerId={companyId}
        checkoutPayload={checkoutPayload}
        uniqueUsers={uniqueUsers}
        onSuccessCallback={() => {
          resetForm();
          toast.success('Service Request Submitted Successfully');
          setTimeout(() => {
            navigate(`/msps/${partnerId}/companies/${companyId}/management/submittedRequests`);
          }, 0);
        }}
      />
      )}
    </>
  );
};
